import {ReactNode} from "react";
import {IconType} from "react-icons";
import {BiSolidNetworkChart} from "react-icons/bi";
import {FaMapMarkerAlt} from "react-icons/fa";
import {FaClone, FaPlus, FaUsers} from "react-icons/fa6";
import {HiBadgeCheck} from "react-icons/hi";
import {RiFileChartLine} from "react-icons/ri";
import {Link} from "react-router";

interface AdditionalFeatureDef {
  title: string;
  subtitle: string;
  icon: IconType;
  supportURL: string;
  accessory: ReactNode;
}

export interface getAdditionalFeaturesProps {
  dashboardURL: string;
  enableStatusPages: boolean;
}

export const getAdditionalFeatures = ({
  dashboardURL,
  enableStatusPages,
}: getAdditionalFeaturesProps): AdditionalFeatureDef[] => {
  let features = [
    {
      title: "Dashboards",
      subtitle: "A customizable statistic overview of checks currently running within your account.",
      icon: FaClone,
      accessory: (
        <a className="title" href={`${dashboardURL}?pop=initial&gobackoncancel=true`}>
          <small>Customize Dashboard</small>
        </a>
      ),
      supportURL: "https://support.uptime.com/hc/en-us/articles/115002557925-Overview-of-Dashboard",
    },
    {
      title: "Private locations",
      subtitle:
        "Monitor internal applications and services using an instance of Uptime.com run from a private probe server.",
      icon: FaMapMarkerAlt,
      accessory: (
        <Link className="title" to={`/contact?subject=User%20request%20for%20Private%20Locations`} target="_blank">
          <small>Request Private Locations</small>
        </Link>
      ),
      supportURL:
        "https://support.uptime.com/hc/en-us/articles/360012622239-Private-Location-Monitoring-Setup-and-Troubleshooting",
    },
    {
      title: "Subaccounts",
      subtitle: "Separate accounts, systems, or teams and manage checks and reports for each account.",
      icon: FaUsers,
      accessory: (
        <Link className="title" to={`/contact?subject=User%20request%20for%20Subaccounts`} target="_blank">
          <small>Request Subaccounts</small>
        </Link>
      ),
      supportURL: "https://support.uptime.com/hc/en-us/articles/360015999479-Uptime-com-Subaccounts",
    },
  ];

  const statusPages = {
    title: "Status pages",
    subtitle:
      "A Status Page conveys info on scheduled maintenance, downtime events, and general system uptime to your site.",
    icon: HiBadgeCheck,
    accessory: (
      <Link className="title d-flex align-items-center" to={`/feature-unavailable?f=statuspages`}>
        <FaPlus className="me-1" />
        <small>Add status page</small>
      </Link>
    ),
    supportURL:
      "https://support.uptime.com/hc/en-us/articles/360016256820-Setup-Instructions-for-your-Uptime-com-Status-Page",
  };

  if (enableStatusPages) {
    statusPages.accessory = (
      <Link className="title d-flex align-items-center" to="/statuspages?pop=initial&gobackoncancel=true">
        <FaPlus className="me-1" />
        <small>Add status page</small>
      </Link>
    );
  }

  features.push(statusPages);

  features = features.concat([
    {
      title: "SLA reports",
      subtitle:
        "Report the uptime and performance data from specific checks or systems for the purposes of SLA accountability.",
      icon: RiFileChartLine,
      accessory: (
        <Link className="title d-flex align-items-center" to={`/reports/sla?pop=initial&gobackoncancel=true`}>
          <FaPlus className="me-1" />
          <small>Create a Report</small>
        </Link>
      ),
      supportURL: "https://support.uptime.com/hc/en-us/articles/360017118860-Creating-an-Uptime-com-SLA-Report-",
    },
    {
      title: "Third-party Integration",
      subtitle: "Use Uptime.com to deliver critical metric and alert data to key tools you trust and use.",
      icon: BiSolidNetworkChart,
      accessory: (
        <Link className="title d-flex align-items-center" to="/integrations/manage?pop=initial&gobackoncancel=true">
          <FaPlus className="me-1" />
          <small>Add integration</small>
        </Link>
      ),
      supportURL: "https://support.uptime.com/hc/en-us/articles/115002534509-Overview-of-Push-Notifications",
    },
  ]);

  return features;
};
